import './assets/css/App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import initialTheme from './theme/theme';
import { useState, lazy, Suspense } from 'react';

// RoofMapper v3 Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { BatchProvider } from './contexts/BatchContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

// RoofMapper v3 Component (provider is small; RoofMapper UI itself is heavy and stays lazy via the route view that uses it)
import { RoofMapperProvider, RoofMapper } from './roofmapper';

// Eager-loaded shells (login, layouts, auth guard, route fallback)
import AuthLayout from './layouts/auth';
import AdminLayout from './layouts/admin';
import RTLLayout from './layouts/rtl';
import AppLayout from './layouts/AppLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PageFallback from './components/PageFallback';

// Lazy-loaded route views - each becomes its own chunk so the initial bundle stays small.
const GeoTiffViewer = lazy(() => import('./views/GeoTiffViewer'));
const GeoTiffPlayground = lazy(() => import('./views/GeoTiffPlayground'));
const GeoTiffPlaygroundWebGL = lazy(() => import('./views/GeoTiffPlaygroundWebGL'));
const GeoTiffPlaygroundGoogleMaps = lazy(() => import('./views/GeoTiffPlaygroundGoogleMaps'));
const SolarAssessment = lazy(() => import('./views/SolarAssessment'));
const SolarAssessmentV2 = lazy(() => import('./views/SolarAssessmentV2'));
const MonthlyFluxViewerPage = lazy(() => import('./views/MonthlyFluxViewerPage'));
const LayoutDemo = lazy(() => import('./views/LayoutDemo'));
const LayoutDemoComplete = lazy(() => import('./views/LayoutDemoComplete'));
const DsmViewer3D = lazy(() => import('./views/DsmViewer3D'));
const BatchReview = lazy(() => import('./views/admin/batches/BatchReview'));
const BatchDetails = lazy(() => import('./views/admin/batches/BatchDetails'));
const UserManagement = lazy(() => import('./views/UserManagement'));
const EmbeddedAssessment = lazy(() => import('./views/EmbeddedAssessment'));

import { env } from 'env';
const GOOGLE_CLIENT_ID = env.GOOGLE_CLIENT_ID || '';

export default function Main() {
  // eslint-disable-next-line
  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ChakraProvider theme={currentTheme}>
        <AuthProvider>
          <BatchProvider>
            <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="auth/*" element={<AuthLayout />} />
          <Route
            path="admin/*"
            element={
              <AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />
            }
          />
          <Route
            path="rtl/*"
            element={
              <RTLLayout theme={currentTheme} setTheme={setCurrentTheme} />
            }
          />
          {/* RoofMapper v3 - Portable component */}
          <Route
            path="/map"
            element={
              <RoofMapperProvider
                config={{
                  apiEndpoint: env.API_URL || 'http://localhost:5001',
                  googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                }}
              >
                <RoofMapper mode="fullscreen" />
              </RoofMapperProvider>
            }
          />
          {/* Solar Assessment - Protected route, requires authentication */}
          <Route
            path="/assessment"
            element={
              <ProtectedRoute>
                <RoofMapperProvider
                  config={{
                    apiEndpoint: env.API_URL || 'http://localhost:5001',
                    googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                  }}
                >
                  <SolarAssessment />
                </RoofMapperProvider>
              </ProtectedRoute>
            }
          />
          {/* Solar Assessment V2 - New layout with AppLayout */}
          <Route
            path="/assessment-v2"
            element={
              <ProtectedRoute>
                <RoofMapperProvider
                  config={{
                    apiEndpoint: env.API_URL || 'http://localhost:5001',
                    googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                  }}
                >
                  <SolarAssessmentV2 />
                </RoofMapperProvider>
              </ProtectedRoute>
            }
          />
          {/* GeoTIFF Viewer - Standalone without admin layout */}
          <Route path="/geotiff-viewer" element={<GeoTiffViewer />} />
          {/* GeoTIFF Playground - WebGL-accelerated viewer (NEW) */}
          <Route
            path="/geotiff-playground"
            element={
              <RoofMapperProvider
                config={{
                  apiEndpoint: env.API_URL || 'http://localhost:5001',
                  googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                }}
              >
                <GeoTiffPlaygroundWebGL />
              </RoofMapperProvider>
            }
          />
          {/* GeoTIFF Playground (Legacy Canvas 2D) */}
          <Route path="/geotiff-playground-legacy" element={<GeoTiffPlayground />} />
          {/* GeoTIFF Playground - Google Maps EXPERIMENTAL */}
          <Route
            path="/geotiff-playground-gmaps"
            element={
              <RoofMapperProvider
                config={{
                  apiEndpoint: env.API_URL || 'http://localhost:5001',
                  googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                }}
              >
                <GeoTiffPlaygroundGoogleMaps />
              </RoofMapperProvider>
            }
          />
          {/* Monthly Flux Viewer - Standalone */}
          <Route path="/geotiff-viewer/monthly-flux" element={<MonthlyFluxViewerPage />} />
          {/* Layout Demo - Preview new HubSpot-style navigation */}
          <Route path="/layout-demo" element={<LayoutDemo />} />
          {/* Layout Demo Complete - Full structure with side panel */}
          <Route path="/layout-demo-complete" element={<LayoutDemoComplete />} />
          {/* DSM 3D Viewer - Interactive terrain visualization */}
          <Route
            path="/dsm-3d-viewer"
            element={
              <RoofMapperProvider
                config={{
                  apiEndpoint: env.API_URL || 'http://localhost:5001',
                  googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                }}
              >
                <DsmViewer3D />
              </RoofMapperProvider>
            }
          />
          {/* Batch Management - Protected routes with AppLayout */}
          <Route
            path="/batches"
            element={
              <ProtectedRoute>
                <AppLayout pageTitle="Batch Management">
                  <BatchReview />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/batches/:batchId"
            element={
              <ProtectedRoute>
                <AppLayout pageTitle="Batch Details">
                  <BatchDetails />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* User Management - Admin only */}
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          {/* Embedded Mode - For Niko Engine iframe integration */}
          <Route
            path="/embed"
            element={
              <RoofMapperProvider
                config={{
                  apiEndpoint: env.API_URL || 'http://localhost:5001',
                  googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
                }}
              >
                <EmbeddedAssessment />
              </RoofMapperProvider>
            }
          />
              {/* Default route - redirect to sign in */}
              <Route path="/" element={<Navigate to="/auth/sign-in/default" replace />} />
            </Routes>
            </Suspense>
          </BatchProvider>
        </AuthProvider>
      </ChakraProvider>
    </GoogleOAuthProvider>
  );
}
// Redeploy trigger lunes,  8 de diciembre de 2025, 22:20:20 CST
